import React, { FC } from 'react';
import { MetricCard, NO_DATA } from '../scaling/scalingCard';
import { baselineTrend, deltaTrend, lowSample, weekLabel } from './series';

/**
 * Headline tile for the Browser Ladder tab: the last COMPLETE week's value,
 * the week-over-week delta, the pre-ladder baseline, and the sample size
 * behind the number. Below MIN_SAMPLE observations (in either the current or
 * the prior week) the value is still shown but not trended, so a two-intent
 * week can't claim a 50-point swing.
 */

export interface ImpactTileProps {
  index: number;
  title: string;
  /** Weekly bucket key ('YYYY-MM-DD' Monday) of the last complete week. */
  weekBucket?: string;
  current: number | null;
  previous: number | null;
  baseline: number | null;
  /** Observations behind `current` (intents, tasks, requests…). */
  sampleN?: number;
  /** Observations behind `previous`; a thin prior week also withholds WoW. */
  previousSampleN?: number;
  sampleUnit: string;
  format: (v: number | null) => string;
  lowerIsBetter: boolean;
  /** Unit suffix for delta labels: '%', 'h', 'm' or ''. */
  suffix?: string;
  details: string;
  /** The in-progress week's value, shown under the headline as a preview. */
  soFar?: { weekBucket: string; value: number | null; sampleN?: number };
}

export const ImpactTile: FC<ImpactTileProps> = ({
  index,
  title,
  weekBucket,
  current,
  previous,
  baseline,
  sampleN,
  previousSampleN,
  sampleUnit,
  format,
  lowerIsBetter,
  suffix = '',
  details,
  soFar,
}) => {
  const thin = lowSample(sampleN);
  const value = weekBucket ? format(current) : NO_DATA;
  const sub = weekBucket
    ? `${weekLabel(weekBucket)} · n=${sampleN ?? 0} ${sampleUnit}${
        thin ? ' · low sample' : ''
      }`
    : 'No complete week in range yet';
  const wow =
    thin || lowSample(previousSampleN)
      ? undefined
      : deltaTrend(current, previous, lowerIsBetter, suffix);
  const vsBaseline = thin
    ? undefined
    : baselineTrend(current, baseline, lowerIsBetter, suffix);
  return (
    <MetricCard
      index={index}
      title={title}
      value={value}
      sub={sub}
      trend={wow}
      className={thin ? 'ladder-tile--thin' : undefined}
      details={<p>{details}</p>}
    >
      {soFar && (
        <p className='ladder-baseline ladder-so-far'>
          This week so far ({weekLabel(soFar.weekBucket)}) {format(soFar.value)}
          {soFar.sampleN != null && (
            <span className='ladder-so-far-n'>
              n={soFar.sampleN} {sampleUnit}
            </span>
          )}
        </p>
      )}
      {baseline != null && (
        <p className='ladder-baseline'>
          Pre-ladder baseline {format(baseline)}
          {vsBaseline && (
            <span className={`scaling-trend ${vsBaseline.direction}`}>
              {vsBaseline.label}
            </span>
          )}
        </p>
      )}
    </MetricCard>
  );
};
